/**
 * CheckInFaceStep - Face enrollment or verification step for check-in.
 * Captures a selfie, calls enrollPatientFace or verifyPatientFace, then onSuccess or shows failure.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { contentCardSx } from '../../styles/standaloneLayoutTokens';
import { PhotoCamera, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { firebaseApp } from '@healthdesk/shared-services';
import { AuthContext } from '@healthdesk/shared-hooks';

const functions = getFunctions(firebaseApp);

/**
 * @param {Object} props
 * @param {'enrollment'|'verification'} props.mode
 * @param {function} props.onSuccess - Called when enrollment or verification succeeds (then parent creates visit)
 */
const CheckInFaceStep = ({ mode, onSuccess }) => {
  const { user } = React.useContext(AuthContext);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [status, setStatus] = useState('camera'); // 'camera' | 'submitting' | 'success' | 'failure'
  const [errorMessage, setErrorMessage] = useState(null);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    setCameraReady(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setCameraReady(true);
        };
      }
    } catch (err) {
      console.warn('[CheckInFaceStep] Camera error:', err);
      setCameraError(err.message || 'Could not access camera');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraReady(false);
  }, []);

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, [startCamera, stopCamera]);

  const captureAndSubmit = async () => {
    if (!videoRef.current || !canvasRef.current || !user?.uid) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (video.readyState < 2) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    const base64 = dataUrl.replace(/^data:image\/\w+;base64,/, '');

    setStatus('submitting');
    setErrorMessage(null);
    stopCamera();

    try {
      if (mode === 'enrollment') {
        const enroll = httpsCallable(functions, 'enrollPatientFace');
        await enroll({ patientId: user.uid, imageBase64: base64 });
        setStatus('success');
        onSuccess?.();
      } else {
        const verify = httpsCallable(functions, 'verifyPatientFace');
        const result = await verify({ patientId: user.uid, imageBase64: base64 });
        if (result.data?.match) {
          setStatus('success');
          onSuccess?.();
        } else {
          setStatus('failure');
          setErrorMessage(
            result.data?.reason === 'not_enrolled'
              ? 'No face on file. Please complete check-in with staff.'
              : "Face didn't match. Please ask a staff member to complete your check-in."
          );
        }
      }
    } catch (err) {
      setStatus('failure');
      const msg = err.message || err.code || 'Something went wrong';
      setErrorMessage(
        mode === 'enrollment'
          ? `Couldn't register your face. ${msg}. Please try again in good light.`
          : `Verification failed. ${msg}. Please try again or ask staff to complete your check-in.`
      );
    }
  };

  const handleTryAgain = () => {
    setStatus('camera');
    setErrorMessage(null);
    startCamera();
  };

  const isEnrollment = mode === 'enrollment';
  const title = isEnrollment
    ? 'Register your face for check-in'
    : 'Confirm it\'s you with a quick selfie';
  const guidance =
    'Face the camera in good light. Remove sunglasses and face forward.';

  if (status === 'success') {
    return (
      <Paper elevation={0} sx={{ ...contentCardSx, p: 4, textAlign: 'center' }}>
        <CheckCircle color="success" sx={{ fontSize: 48, mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          {isEnrollment ? 'Face registered' : 'Verified. You\'re checked in.'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isEnrollment
            ? 'Future check-ins will use it to verify it\'s you.'
            : 'Completing your check-in...'}
        </Typography>
      </Paper>
    );
  }

  if (status === 'failure') {
    return (
      <Paper elevation={0} sx={{ ...contentCardSx, p: 4, textAlign: 'center' }}>
        <ErrorIcon color="error" sx={{ fontSize: 48, mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          {isEnrollment ? 'Registration failed' : 'Face didn\'t match'}
        </Typography>
        {errorMessage && (
          <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
            {errorMessage}
          </Alert>
        )}
        <Button variant="contained" onClick={handleTryAgain}>
          Try again
        </Button>
      </Paper>
    );
  }

  if (status === 'submitting') {
    return (
      <Paper elevation={0} sx={{ ...contentCardSx, p: 4, textAlign: 'center' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          {isEnrollment ? 'Registering your face...' : 'Verifying...'}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ ...contentCardSx, p: 4 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {guidance}
      </Typography>
      {cameraError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {cameraError}
        </Alert>
      )}
      <Box sx={{ position: 'relative', bgcolor: 'grey.900', borderRadius: 1, overflow: 'hidden', mb: 2 }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{ width: '100%', display: 'block' }}
        />
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </Box>
      <Button
        variant="contained"
        startIcon={<PhotoCamera />}
        onClick={captureAndSubmit}
        disabled={!cameraReady}
      >
        Take photo
      </Button>
    </Paper>
  );
};

export default CheckInFaceStep;
